import React, { FC, useEffect } from 'react'
import { matchPath, useHistory, useLocation } from 'react-router'
import { paths } from 'routes'
import { AuthStorageInstance } from 'services/Storage/AuthStorage'
import { useAbExperiment } from 'utils/useAbExperiment'
import { useSubscription } from 'utils/useSubscription'
import { isMobileDevice } from 'utils/isMobile'

// setTimeout stores the delay as a 32-bit signed int; delays beyond this
// (~24.8 days) overflow and fire almost immediately.
const MAX_TIMEOUT_MS = 2147483647

// Phones only get the promo login page — every ledger route (including '/')
// bounces to /login. Everything outside `paths` (webview/link pages like
// orderQR, easypay, staff-order, /blocked, /subscription-required) stays
// reachable from mobile devices. defaulterLink stays allowed so the app-block
// redirect below doesn't fight this one in a replace loop.
const DESKTOP_ONLY_PATHS = Object.values(paths).filter(
  (path) => path !== paths.mobileNotSupported && path !== paths.defaulterLink
)

const WithValidation = (WrappedComponent: FC) => {
  const ComponentWithValidation = (props: any) => {
    const { okTermLoanAppBlock, ready } = useAbExperiment()
    const history = useHistory()
    const location = useLocation()
    const isLoggedIn = AuthStorageInstance.getIsLoggedIn()
    const path = location.pathname !== paths.defaulterLink
    const isMobileBlockPage = location.pathname === paths.mobileNotSupported

    const isLoginPage =
      location.pathname === '/auth' ||
      location.pathname === '/login' ||
      location.pathname.startsWith('/auth/') ||
      location.pathname.startsWith('/login/')
    const skipSubscriptionCheck = !isLoggedIn || isLoginPage
    const {
      hasActiveSubscription,
      ready: subscriptionReady,
      expiryTime,
      checkAndRefreshIfExpired
    } = useSubscription(skipSubscriptionCheck)
    useEffect(() => {
      if (!isMobileDevice()) {
        return
      }
      if (isMobileBlockPage) {
        // Legacy links (old campaigns, bookmarks) pointed at the block page;
        // send them into the login flow instead.
        history.replace('/login')
        return
      }
      const isDesktopOnlyRoute = !!matchPath(location.pathname, {
        path: DESKTOP_ONLY_PATHS,
        exact: true
      })
      if (isDesktopOnlyRoute) {
        history.replace('/login')
      }
    }, [location.pathname, isMobileBlockPage, history])

    useEffect(() => {
      const shouldRedirect = ready && okTermLoanAppBlock && isLoggedIn && path
      if (shouldRedirect) {
        history.replace(paths.defaulterLink)
      }
    }, [ready, okTermLoanAppBlock, isLoggedIn, path, history])

    useEffect(() => {
      if (isLoginPage) {
        return
      }

      const shouldRedirectToSubscriptionBlock =
        subscriptionReady && !hasActiveSubscription && isLoggedIn
      if (shouldRedirectToSubscriptionBlock) {
        history.replace('/subscription-required')
      }
    }, [subscriptionReady, hasActiveSubscription, isLoginPage, isLoggedIn, history])

    useEffect(() => {
      if (!isLoggedIn || !expiryTime || !hasActiveSubscription) {
        return
      }

      const expiryMs = expiryTime * 1000
      const now = Date.now()
      const timeUntilExpiry = expiryMs - now

      if (timeUntilExpiry <= 0) {
        history.replace('/subscription-required')
        return
      }
      // A delay larger than the 32-bit limit overflows and fires immediately,
      // which would wrongly evict an active subscriber. Skip arming the auto
      // redirect for long-lived subscriptions; checkAndRefreshIfExpired catches
      // their expiry on the next navigation instead.
      if (timeUntilExpiry > MAX_TIMEOUT_MS) {
        return
      }
      const timer = setTimeout(() => {
        history.replace('/subscription-required')
      }, timeUntilExpiry)

      return () => {
        clearTimeout(timer)
      }
    }, [expiryTime, isLoggedIn, hasActiveSubscription, history])

    useEffect(() => {
      if (!isLoggedIn) {
        return
      }
      checkAndRefreshIfExpired()
    }, [location.pathname, isLoginPage, isLoggedIn, checkAndRefreshIfExpired])

    return <WrappedComponent {...props} />
  }

  return ComponentWithValidation
}

export default WithValidation
